import DOMPurify from 'dompurify';

/**
 * Extracts plain text from HTML string
 *
 * Uses DOMPurify for sanitization and native DOM APIs for text extraction.
 * Safely handles HTML content by creating a temporary DOM element.
 *
 * @param {string} html - HTML string to extract text from
 * @returns {string} Plain text content without HTML tags
 *
 * @example
 * extractPlainTextFromHtml('<p>Hello <strong>World</strong></p>')
 * // Returns: 'Hello World'
 *
 * extractPlainTextFromHtml('<div>Line 1<br>Line 2</div>')
 * // Returns: 'Line 1\nLine 2'
 *
 * extractPlainTextFromHtml('')
 * // Returns: ''
 *
 * extractPlainTextFromHtml(null)
 * // Returns: ''
 */
export const extractPlainTextFromHtml = (html) => {
  // Handle null, undefined, or empty string
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Sanitize HTML to prevent XSS
  const sanitized = DOMPurify.sanitize(html);

  // Create temporary DOM element to extract text
  const temp = document.createElement('div');
  temp.innerHTML = sanitized;

  // Extract text content (textContent is more reliable than innerText)
  const text = temp.textContent || temp.innerText || '';

  // Trim and return
  return text.trim();
};
